/**
 * Helpers for surfacing performance preferences AND detecting conflicts
 * across a contestant's visible entries on the dashboard / event-detail
 * pages.
 *
 * "Conflict" here is informational, not blocking. The user might
 * intentionally have different picks across multi-reg entries, or their
 * team-roping partner might have picked a different slot than the
 * buddy-group entry. The UI just surfaces it so they aren't surprised
 * later.
 *
 * Two conflict shapes:
 *   1. Multi-entry: two+ active entries the user can SEE for the same
 *      event have different first preferences. Common when team-roping
 *      multi-reg is enabled — a contestant might enter once in their
 *      buddy group and again with an outside partner.
 *   2. Team-partner: an entry's partner_first_performance_preference
 *      differs from the entry's own first_performance_preference. Backend
 *      enriches partner_*_preference for every row that has a paired
 *      partner_registration_id, so this works for both sides of a team.
 */
import { formatPreferenceLabel } from './format';

/** Loose shape — both registration-ops + dashboard-data return these. */
export interface EntryWithPreferences {
  id?: string;
  registration_id?: string;
  event_id?: string;
  first_performance_preference?: string | null;
  second_performance_preference?: string | null;
  resolved_performance_slot?: string | null;
  /**
   * The contestant's ACTUAL placement in the latest FINALIZED draw — the
   * performance name read straight from the draw result (same source as the
   * day sheet / finalize email / public results). Authoritative over
   * `resolved_performance_slot`, which is only the preference-resolver's
   * INPUT and can diverge from where the draw actually placed them.
   * Absent until the draw is finalized.
   */
  assigned_performance_name?: string | null;
  assigned_performance_is_slack?: boolean;
  partner_first_performance_preference?: string | null;
  partner_second_performance_preference?: string | null;
  partner_resolved_performance_slot?: string | null;
  partner_name?: string | null;
  registration_type?: string | null;
  partner_registration_id?: string | null;
  // Dashboard payload nests rodeo info under `rodeo`; registration-ops
  // ships rodeo only via the trig_rodeo_name string. Tolerate both so the
  // grouping helpers below work for either source without a transform
  // step at the call site.
  rodeo?: { id?: string | null; name?: string | null } | null;
  // Tolerate extras (the registration-ops payload carries a lot more).
  [key: string]: unknown;
}

/** Strip the `[isoDatetime]` disambiguation suffix and lowercase. */
const normalize = (value: string | null | undefined): string => {
  if (value == null) return '';
  return value.trim().replace(/\s*\[[^\]]*\]\s*$/, '').toLowerCase();
};

/** Two preference values are "the same" when they normalize equally. */
const samePref = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => normalize(a) === normalize(b);

/** True when the entry has any preference data worth displaying. */
export function hasAnyPreference(entry: EntryWithPreferences): boolean {
  return !!(
    entry.first_performance_preference ||
    entry.second_performance_preference ||
    entry.resolved_performance_slot
  );
}

export interface PreferenceConflictInfo {
  hasConflict: boolean;
  /**
   * Short, one-line copy describing the type of mismatch — drives the
   * tooltip / muted helper text under the warning badge. Empty string
   * when `hasConflict` is false.
   */
  reason: string;
  /**
   * 'team_partner' = the entry's partner picked a different slot. Most
   * common case; copy notes this is "expected" if the partner is outside
   * the buddy group.
   *
   * 'multi_entry' = the user has two+ active entries on the same event
   * with different first preferences.
   *
   * 'both' = both shapes are present.
   */
  kind: 'team_partner' | 'multi_entry' | 'both' | null;
}

const NO_CONFLICT: PreferenceConflictInfo = {
  hasConflict: false,
  reason: '',
  kind: null,
};

/**
 * Detect conflicts among a list of entries that all belong to the SAME
 * event. Caller is responsible for grouping by event_id before calling.
 *
 * Returns NO_CONFLICT when the list is empty / all preferences match /
 * preferences are blank.
 *
 * Optional `timezone`: parent rodeo's IANA zone. When provided every
 * preference label rendered into the human-readable `reason` string is
 * pinned to that zone with the abbreviation appended ("Fri, May 15,
 * 6:00 PM PT") so the warning copy doesn't drift with the viewer's
 * locale.
 */
export function detectConflictsForEvent(
  entries: EntryWithPreferences[],
  timezone?: string | null,
): PreferenceConflictInfo {
  if (!entries || entries.length === 0) return NO_CONFLICT;

  const reasons: string[] = [];
  let teamPartnerConflict = false;
  let multiEntryConflict = false;

  // Team-partner mismatch: any single entry whose partner pref ≠ its own
  // pref, on either the 1st OR 2nd choice. We bail after the first
  // mismatch we detect to keep copy short — and prefer the 1st-choice
  // wording when both differ since that's the higher-priority pick.
  for (const e of entries) {
    if (e.partner_registration_id == null) continue;
    const ownFirst = e.first_performance_preference;
    const partnerFirst = e.partner_first_performance_preference;
    const ownSecond = e.second_performance_preference;
    const partnerSecond = e.partner_second_performance_preference;

    const firstBothEmpty =
      (!ownFirst || normalize(ownFirst) === '') &&
      (!partnerFirst || normalize(partnerFirst) === '');
    const secondBothEmpty =
      (!ownSecond || normalize(ownSecond) === '') &&
      (!partnerSecond || normalize(partnerSecond) === '');

    const firstDiffers = !firstBothEmpty && !samePref(ownFirst, partnerFirst);
    const secondDiffers =
      !secondBothEmpty && !samePref(ownSecond, partnerSecond);

    if (firstDiffers || secondDiffers) {
      teamPartnerConflict = true;
      const label = e.partner_name ? `your partner ${e.partner_name}` : 'your partner';
      // Prefer the 1st-choice line when it's the one that differs.
      // Otherwise surface the 2nd-choice mismatch so the user sees a
      // concrete diff rather than a vague headline.
      if (firstDiffers) {
        reasons.push(
          `Your 1st preference (${formatPreferenceLabel(ownFirst, timezone) || '—'}) differs from ${label}'s (${formatPreferenceLabel(partnerFirst, timezone) || '—'}).`,
        );
      } else {
        reasons.push(
          `Your 2nd preference (${formatPreferenceLabel(ownSecond, timezone) || '—'}) differs from ${label}'s (${formatPreferenceLabel(partnerSecond, timezone) || '—'}).`,
        );
      }
      break; // one team-partner reason is enough; UI keeps copy short
    }
  }

  // Multi-entry mismatch: 2+ entries in this group with different 1st
  // OR 2nd preferences. The grouping key is decided by the caller
  // (event for the EventDetailPage, rodeo for the dashboard) —
  // performance preferences are practically rodeo-level so dashboard
  // callers should always group by rodeo, otherwise a contestant
  // entered in two events at the same rodeo with mismatched picks
  // won't be flagged. We surface 1st-choice and 2nd-choice mismatches
  // separately so the user can see exactly which slot disagrees.
  if (entries.length > 1) {
    const firstDistinct = new Set(
      entries
        .map((e) => normalize(e.first_performance_preference))
        .filter((v) => v.length > 0),
    );
    const secondDistinct = new Set(
      entries
        .map((e) => normalize(e.second_performance_preference))
        .filter((v) => v.length > 0),
    );

    const firstMismatch = firstDistinct.size > 1;
    const secondMismatch = secondDistinct.size > 1;

    if (firstMismatch && secondMismatch) {
      multiEntryConflict = true;
      reasons.push(
        'Your entries use different 1st and 2nd performance preferences.',
      );
    } else if (firstMismatch) {
      multiEntryConflict = true;
      reasons.push(
        'Your entries use different 1st performance preferences.',
      );
    } else if (secondMismatch) {
      multiEntryConflict = true;
      reasons.push(
        'Your entries use different 2nd performance preferences.',
      );
    }
  }

  if (!teamPartnerConflict && !multiEntryConflict) return NO_CONFLICT;

  const kind: PreferenceConflictInfo['kind'] =
    teamPartnerConflict && multiEntryConflict
      ? 'both'
      : teamPartnerConflict
        ? 'team_partner'
        : 'multi_entry';

  return {
    hasConflict: true,
    reason: reasons.join(' '),
    kind,
  };
}

/**
 * Group a flat list of entries by event_id. Convenience for the dashboard
 * card where /my/upcoming-events returns a flat array.
 */
export function groupEntriesByEvent<T extends EntryWithPreferences>(
  entries: T[] | undefined,
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  if (!entries) return map;
  for (const entry of entries) {
    const eventId =
      ((entry as any).event && (entry as any).event.id) ||
      (entry.event_id as string | undefined);
    if (!eventId) continue;
    const list = map.get(eventId);
    if (list) list.push(entry);
    else map.set(eventId, [entry]);
  }
  return map;
}

/**
 * Group a flat list of entries by `rodeo.id`. Performance preferences
 * are stored per registration but are conceptually rodeo-wide — a
 * contestant entering Bareback Riding AND Barrel Racing at one rodeo
 * with mismatched 1st preferences is a real conflict that the dashboard
 * needs to flag, even though each registration owns its own pref column.
 */
export function groupEntriesByRodeo<T extends EntryWithPreferences>(
  entries: T[] | undefined,
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  if (!entries) return map;
  for (const entry of entries) {
    const rodeoId =
      (entry.rodeo && entry.rodeo.id) ||
      ((entry as any).rodeo_id as string | undefined);
    if (!rodeoId) continue;
    const list = map.get(rodeoId);
    if (list) list.push(entry);
    else map.set(rodeoId, [entry]);
  }
  return map;
}
